import { Divider, useDisclosure, VStack } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { useApiClient } from "../../hooks/useApiClient";
import { SegmentApiClient } from "../../api/apiClientSegment";
import { MemberFilterQueryModal } from "../../components/MemberFilterQueryModal/MemberFilterQueryModal";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { NotificationFormValues } from "./types";
import { SavedSegmentsSection } from "./SavedSegmentsSection";
import { ManualFiltersSection } from "./ManualFiltersSection";
import { SelectSegmentModal } from "./SelectSegmentModal";
import { CherryPayApi } from "../../api/models";
import { useSpamWarning } from "../../hooks/useSpamWarning";
import { SpamWarningBanner } from "./SpamWarningBanner";
import { CollapsibleSection } from "./CollapsibleSection";

interface EligibleMembersProps {
  businessId: string;
  notificationScheduleId?: string | null;
  isOpen: boolean;
  onToggle: () => void;
}

export const EligibleMembers: React.FC<EligibleMembersProps> = ({
  businessId,
  notificationScheduleId,
  isOpen,
  onToggle,
}) => {
  const { values, setFieldValue } = useFormikContext<NotificationFormValues>();
  const [filterQuery, setFilterQuery] = useState<string>(
    values.memberFilterQuery
  );
  const [segmentName, setSegmentName] = useState<string>(
    values.memberFilterSegment?.SegmentName ?? ""
  );
  const [modalSegmentMode, setModalSegmentMode] = useState(false);
  const [modalSegmentId, setModalSegmentId] = useState<string | null>(null);
  const [hasSegments, setHasSegments] = useState(false);
  const apiClient = useApiClient();
  const segmentApiClient = useMemo(
    () => new SegmentApiClient(apiClient.cherryPayClient),
    [apiClient]
  );

  const refreshHasSegments = () => {
    segmentApiClient
      .getMemberFilterSegments(businessId, { limit: 1 })
      .then((result) => {
        if (result.ok) {
          setHasSegments((result.data.Results?.length ?? 0) > 0);
        }
      });
  };

  useEffect(() => {
    refreshHasSegments();
  }, [businessId, segmentApiClient]);

  const spamWarning = useSpamWarning(
    businessId,
    values.filterMode,
    values.memberFilterSegment,
    filterQuery
  );

  const memberFilterQueryModal = useDisclosure();
  const selectSegmentModal = useDisclosure();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const openSegmentModal = () => {
    setModalSegmentMode(true);
    setModalSegmentId(null);
    memberFilterQueryModal.onOpen();
  };

  const openEditSegmentModal = () => {
    setModalSegmentMode(true);
    setModalSegmentId(values.memberFilterSegment?.SegmentId ?? null);
    memberFilterQueryModal.onOpen();
  };

  const openManualModal = () => {
    setModalSegmentMode(false);
    setModalSegmentId(null);
    memberFilterQueryModal.onOpen();
  };

  const onSuccessMemberFilterQuery = (
    query: string | undefined,
    name?: string,
    segmentId?: string
  ) => {
    if (query !== undefined) {
      const isEmpty = !modalSegmentMode && !query;
      setFilterQuery(query);
      setFieldValue("memberFilterQuery", query);
      setFieldValue(
        "filterMode",
        isEmpty ? null : modalSegmentMode ? "segment" : "manualFilter"
      );
      if (modalSegmentMode && name && segmentId) {
        setSegmentName(name);
        setHasSegments(true);
        setFieldValue("memberFilterSegment", {
          SegmentId: segmentId,
          SegmentName: name,
        });
      } else {
        setFieldValue("memberFilterSegment", null);
      }
    }
    memberFilterQueryModal.onClose();
  };

  const onSelectSegment = (
    segment: CherryPayApi.MemberFilterSegmentSummaryItem
  ) => {
    const query = segment.MemberFilter ?? "";
    const name = segment.SegmentName ?? "";
    const id = segment.Meta?.Id ?? "";
    setFilterQuery(query);
    setSegmentName(name);
    setFieldValue("memberFilterQuery", query);
    setFieldValue("filterMode", "segment");
    setFieldValue("memberFilterSegment", {
      SegmentId: id,
      SegmentName: name,
    });
    selectSegmentModal.onClose();
  };

  const onClearSegment = () => {
    setSegmentName("");
    setFilterQuery("");
    setFieldValue("memberFilterQuery", "");
    setFieldValue("memberFilterSegment", null);
    setFieldValue("filterMode", null);
  };

  const onClearFilters = async () => {
    const { ok } = await showConfirmationDialog(
      "Clear all manual filters?",
      "All manually applied filters will be removed from this audience.\nThis action cannot be undone unless filters are added again.",
      "Clear Filters"
    );
    if (ok) {
      setFilterQuery("");
      setFieldValue("memberFilterQuery", "");
      setFieldValue("memberFilterSegment", null);
      setFieldValue("filterMode", null);
    }
  };

  return (
    <>
      <CollapsibleSection
        title="Eligible Members"
        isOpen={isOpen}
        onToggle={onToggle}
      >
        <VStack w="100%" alignItems="flex-start" spacing={4}>
          {values.filterMode !== "manualFilter" && (
            <>
              <SavedSegmentsSection
                filterMode={values.filterMode}
                filterQuery={filterQuery}
                segmentName={segmentName}
                hasSegments={hasSegments}
                onOpenModal={openSegmentModal}
                onOpenSelectModal={selectSegmentModal.onOpen}
                onClearSegment={onClearSegment}
                onEditSegment={
                  values.memberFilterSegment?.SegmentId
                    ? openEditSegmentModal
                    : undefined
                }
              />
              {values.filterMode === "segment" && (
                <SpamWarningBanner
                  {...spamWarning}
                  filterMode={values.filterMode}
                />
              )}
            </>
          )}

          {values.filterMode === null && <Divider />}

          {values.filterMode !== "segment" && (
            <>
              <ManualFiltersSection
                filterMode={values.filterMode}
                filterQuery={filterQuery}
                onOpenModal={openManualModal}
                onClearFilters={onClearFilters}
              />
              {(values.filterMode === "manualFilter" ||
                values.filterMode === null) && (
                <SpamWarningBanner
                  {...spamWarning}
                  filterMode={values.filterMode}
                />
              )}
            </>
          )}
        </VStack>
      </CollapsibleSection>

      {confirmationElement}
      <SelectSegmentModal
        {...selectSegmentModal}
        businessId={businessId}
        onSelect={onSelectSegment}
        notificationScheduleId={notificationScheduleId}
        onSegmentDeleted={(deletedId: string) => {
          if (values.memberFilterSegment?.SegmentId === deletedId) {
            onClearSegment();
          }
          refreshHasSegments();
        }}
      />
      <MemberFilterQueryModal
        size="4xl"
        {...memberFilterQueryModal}
        businessId={businessId}
        memberQueryFilter={filterQuery ?? ""}
        onSuccess={onSuccessMemberFilterQuery}
        segmentMode={modalSegmentMode}
        segmentId={modalSegmentId}
        excludeNotificationScheduleId={notificationScheduleId}
      />
    </>
  );
};
